import { AnyType } from '@src/models/common';
import { isEqual } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LegoV2Context } from './context';
import { getSubscription } from './utils';
import { useEventCallback } from '@src/hooks/useEventCallback';

export const useLegoV2Context = () => {
  return useContext(LegoV2Context);
};

/**
 *  Хук для синхронизации значения с контроллером
 *  Основной хук для работы lego элементов
 *  id - уникальный идентификатор для lego элемента
 */
export const useLegoElementValue = <T = AnyType>(id: string) => {
  const { controller } = useLegoV2Context();

  const [value, setValue] = useState<T>(controller.getValues()?.[id]);

  const result = useRef({});

  useEffect(() => {
    const subscription = getSubscription(controller.valueList$, id, setValue);

    return () => {
      subscription?.unsubscribe();
    };
  }, [controller.valueList$, id]);

  const onChange = useEventCallback((value: T) => controller.onChange(id, value));

  return Object.assign(result.current, { value, onChange });
};

/**
 *  Хук для синхронизации значения с контроллером
 *  Основной хук для работы lego элементов
 *  id - уникальный идентификатор для lego элемента
 */
export const useLegoElementError = (id: string) => {
  const { controller } = useLegoV2Context();
  const [errorMessage, setErrorMessage] = useState<string[]>(controller.getErrors()?.[id]);

  const result = useRef({});

  useEffect(() => {
    const subscription = getSubscription(controller.errorList$, id, setErrorMessage);

    return () => {
      subscription?.unsubscribe();
    };
  }, [controller.errorList$, controller.valueList$, id]);

  return Object.assign(result.current, { errorMessage });
};

/**
 *  Хук для подписки на изменения внешних зависимостей
 *  `id` - это уникальный id lego элемента
 *  `externalDependenceList` - список ключей у объекта с внешними зависимостями на которые вы подписываетесь
 */
export const useExternalDependenceValues = (externalDependenceList: string[] = []) => {
  const { controller } = useLegoV2Context();
  const [value, setValue] = useState<{ [x: string]: AnyType }>(controller.getExternalDependencies());

  /** Это проверка значений массива на случай если будет меняться ссылка */
  const depArray = useRef(externalDependenceList);
  const dependenceList = useMemo(() => {
    if (!isEqual(depArray.current, externalDependenceList)) {
      depArray.current = externalDependenceList;
    }

    return depArray.current;
  }, [externalDependenceList]);

  useEffect(() => {
    const subscriptionList = dependenceList.map((depId) =>
      getSubscription(controller.externalDependenceList$, depId, (v) => setValue((prev) => ({ ...prev, [depId]: v }))),
    );

    return () => {
      subscriptionList.forEach((s) => s.unsubscribe());
    };
  }, [controller, dependenceList]);

  return value;
};

/**
 * Перетащить в `LegoV2Hooks` если будет актуально
 *
 * id - ID лего текущего лего элемента
 * cb - колбек который будет вызываться при изменении зависимости
 * dep - значение зависимости
 *
 * Интересный хук для селектов или других элементов имеющих зависимости от других элементов
 * Данный хук вызывает колбек если изменилась зависимость и не делает это при инициализации, чтобы не потерять начальное значение
 * Также он обнуляет значение текущего элемента, если зависимость изменилась
 *
 *
 * Саымый яркий пример использования, если у вас есть селект которому нужно подгружать опции в зависимости
 * от значения какого-либо поля
 *
 * **Важно мемоизировать ссылку на колбек**
 */
export const useCallbackElementDepend = ({ cb, dep, id }: { id: string; dep: AnyType; cb?: () => void }) => {
  const { onChange } = useLegoElementValue(id);

  const ref = useRef(dep);
  const init = useRef(false);

  useEffect(() => {
    /** Значит изменился и это не инициализация */
    if (ref.current !== dep) {
      cb?.();
      ref.current = dep;

      /** Обнуляем значение, т.к. родитель изменился */
      if (init.current) {
        onChange(undefined);
      }
      /** значит инициализация, если есть дефолтное значение, вызываем колбек */
    } else if (dep !== null && typeof dep !== 'undefined' && !isNaN(dep)) {
      cb?.();
    }

    init.current = true;
  }, [cb, dep, id, onChange]);
};
